/**
 * Zod schema for description (textarea) fields.
 * Messages: Required, Too short, Too long, Invalid characters.
 */

import { z } from 'zod';
import type { FieldSchemaConfig } from '../types';
import { getCharacterLimit, defaultDescriptionMaxChar } from './characterLimit';

export function getDescriptionSchema(col: FieldSchemaConfig): z.ZodTypeAny {
  const required = 'required' in col && col.required === true;
  const minLength = col.minLength ?? undefined;
  const maxChar = getCharacterLimit(col) ?? defaultDescriptionMaxChar;
  const lettersNumbersOnly = col.lettersNumbersOnly === true;

  let schema: z.ZodTypeAny = z
    .union([z.string(), z.number(), z.null(), z.undefined()])
    .transform((v: string | number | null | undefined) => (v == null ? '' : String(v)).trim());

  if (required) {
    schema = schema.refine((s: string) => s.length >= 1, 'This field is required.') as z.ZodTypeAny;
  }
  if (minLength != null && minLength > 0) {
    schema = schema.refine(
      (s: string) => s.length === 0 || s.length >= minLength,
      `Enter at least ${minLength} characters.`
    ) as z.ZodTypeAny;
  }
  schema = schema.refine(
    (s: string) => s.length <= maxChar,
    `Must be ${maxChar} characters or fewer.`
  ) as z.ZodTypeAny;
  if (lettersNumbersOnly) {
    schema = schema.refine(
      (s: string) => s.length === 0 || /^[A-Za-z0-9]*$/.test(s),
      'Only letters and numbers are allowed.'
    ) as z.ZodTypeAny;
  }

  return schema;
}
